import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import Paper from '@mui/material/Paper';
import Snackbar from '@mui/material/Snackbar';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  formatWorkflowStatus,
  getStatusChipColor,
} from '../../utils/workflow';
import { PageTitle } from '../../components/StyledComponents';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  useDeleteApiTarsByIdMutation,
  useGetApiApprovalQueueQuery,
  useGetApiTarsByIdQuery,
  useGetApiUsersMeQuery,
  usePostApiTarsByIdSubmitMutation,
} from '../../app/athenaApiGen';
import AuditLog from '../../components/AuditLog/AuditLog';
import DynamicForm from '../../components/dynamic-form/DynamicForm';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import {
  createFormDataChangeHandler,
  parseRawFormData,
  toNumber,
} from '../../dashboard/components/formUtils';
import useSchemaForm from '../../hooks/useSchemaForm';
import OwlIcon from '../../images/owl.svg';
import { imcsTarFormSchema } from './IMCSTARFormSchema';

export default function IMCSTARForm() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: meData } = useGetApiUsersMeQuery();
  const [formData, setFormData, isLoading, resetFormData] =
    useSchemaForm('IMCSTARFormSchema');
  const { data: tarResp, isFetching: isFetchingTAR } = useGetApiTarsByIdQuery(
    { id: id || '' },
    { skip: !id },
  );
  const [deleteTar, { isLoading: deleting }] = useDeleteApiTarsByIdMutation();
  const [submitTar] = usePostApiTarsByIdSubmitMutation();
  const [deleteDialogOpen, setDeleteDialogOpen] = React.useState(false);
  const [snackbar, setSnackbar] = React.useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const isCloneMode = searchParams.get('mode') === 'clone';
  const [savedTarId, setSavedTarId] = React.useState(isCloneMode ? null : (id || null));
  const [mileageRateInitialized, setMileageRateInitialized] = React.useState(false);

  // Always start fresh on no-id route navigations like /tar-imcs
  useEffect(() => {
    if (id) return;
    resetFormData();
    setSavedTarId(null);
    setMileageRateInitialized(false);
    setDeleteDialogOpen(false);
    setSnackbar({ open: false, message: '', severity: 'success' });
  }, [id, location.key, resetFormData]);

  // Seed TAR Date to today on new forms (creation date). Runs after the
  // reset above so it doesn't get clobbered. Skipped on edit since the
  // saved tarDate hydrates from the API response.
  useEffect(() => {
    if (id) return;
    if (formData?.tarDate) return;
    setFormData((prev) => ({ ...prev, tarDate: dayjs() }));
  }, [id, formData?.tarDate, setFormData]);

  // Derive `subject` (Trip Title) from traveler + destination + trip
  // dates. The Trip Title field was removed from the UI but `subject`
  // still feeds the TAR list view's "Trip Title" column, so we
  // auto-compose it as: `<traveler>_<destination>_<startDate>_<endDate>`
  // (dates formatted YYYY-MM-DD for sortability).
  useEffect(() => {
    const traveler = (formData?.fromContactId || '').toString().trim();
    const destination = (formData?.eventLocation || '').toString().trim();
    const startRaw = formData?.eventStartDate;
    const endRaw = formData?.eventEndDate;
    const fmt = (d) => {
      if (!d) return '';
      const m = dayjs.isDayjs(d) ? d : dayjs(d);
      return m.isValid() ? m.format('YYYY-MM-DD') : '';
    };
    const next = [traveler, destination, fmt(startRaw), fmt(endRaw)]
      .map((s) => s.replace(/\s+/g, '-'))
      .join('_');
    if ((formData?.subject || '') === next) return;
    setFormData((prev) => ({ ...prev, subject: next }));
  }, [
    formData?.fromContactId,
    formData?.eventLocation,
    formData?.eventStartDate,
    formData?.eventEndDate,
    formData?.subject,
    setFormData,
  ]);

  // Seed Name of Traveler from the logged-in user's Contact display
  // name (falling back to userName) for new TARs, plus the traveler's
  // contact UUID (travelerContactId) which feeds itineraries[].travelerId
  // in the schema transform. The name field is read-only, so this is the
  // only source. Skipped on edit — existing TARs preserve their saved data.
  useEffect(() => {
    if (id) return;
    if (formData?.fromContactId) return;
    const displayName = meData?.contact?.name || meData?.userName;
    if (!displayName) return;
    setFormData((prev) => ({
      ...prev,
      fromContactId: displayName,
      travelerContactId: meData?.contact?.contactId,
    }));
  }, [id, formData?.fromContactId, meData, setFormData]);

  // Seed Trip Start to today + 14 days for new TARs (one-shot). Existing
  // TARs hydrate from the API and skip this seed.
  useEffect(() => {
    if (id) return;
    if (formData?.eventStartDate) return;
    setFormData((prev) => ({ ...prev, eventStartDate: dayjs().add(14, 'day') }));
  }, [id, formData?.eventStartDate, setFormData]);

  // Default Trip End to Trip Start + 7 days. The ref tracks the last
  // auto-computed value so manual user edits stick: if formData.eventEndDate
  // matches the previous auto value (or is empty), we recompute when
  // Trip Start changes. If the user typed something else, we leave it.
  const lastAutoTripEndRef = useRef(null);
  useEffect(() => {
    if (!formData?.eventStartDate) return;
    const tripStartObj = dayjs(formData.eventStartDate);
    if (!tripStartObj.isValid()) return;
    const nextAutoEnd = tripStartObj.add(7, 'day');
    setFormData((prev) => {
      const lastAuto = lastAutoTripEndRef.current;
      const userTouched =
        !!prev.eventEndDate && (!lastAuto || !dayjs(prev.eventEndDate).isSame(lastAuto, 'day'));
      if (userTouched) return prev;
      lastAutoTripEndRef.current = nextAutoEnd;
      return { ...prev, eventEndDate: nextAutoEnd };
    });
  }, [formData?.eventStartDate, setFormData]);

  // Compute # of Nights and # of Days from Trip Start / Trip End.
  //   nights = e.diff(s, 'day')         e.g. Jun 1 → Jun 3  ⇒  2 nights
  //   days   = nights + 1                e.g. Jun 1 → Jun 3  ⇒  3 days
  // Negative or invalid spans render empty so the fields don't show
  // "-2" or "NaN".
  useEffect(() => {
    const start = formData?.eventStartDate;
    const end = formData?.eventEndDate;
    if (!start || !end) {
      if (formData?.numberOfNights || formData?.numberOfTripDays) {
        setFormData((prev) => ({
          ...prev,
          numberOfNights: '',
          numberOfTripDays: '',
        }));
      }
      return;
    }
    const s = dayjs(start);
    const e = dayjs(end);
    if (!s.isValid() || !e.isValid()) return;
    const nights = e.diff(s, 'day');
    const nextNights = nights > 0 ? String(nights) : '';
    const nextDays = nights > 0 ? String(nights + 1) : '';
    if (
      formData?.numberOfNights !== nextNights ||
      formData?.numberOfTripDays !== nextDays
    ) {
      setFormData((prev) => ({
        ...prev,
        numberOfNights: nextNights,
        numberOfTripDays: nextDays,
      }));
    }
  }, [
    formData?.eventStartDate,
    formData?.eventEndDate,
    formData?.numberOfNights,
    formData?.numberOfTripDays,
    setFormData,
  ]);

  // As the user tabs down the form, keep the focused field anchored in
  // the upper portion of the viewport so the field's section heading
  // (and any context above it) stays visible. Skipped when the field is
  // already in the comfortable top zone — tabbing through sub-sections
  // (e.g. MM → DD → YYYY inside a date picker) doesn't bounce the page.
  useEffect(() => {
    const handleFocus = (e) => {
      const target = e.target;
      if (!target || typeof target.getBoundingClientRect !== 'function') return;
      const tag = target.tagName;
      if (tag !== 'INPUT' && tag !== 'TEXTAREA' && tag !== 'SELECT') return;
      const rect = target.getBoundingClientRect();
      const viewportH = window.innerHeight;
      // Already in the comfortable top zone (between 80px and ~35% from
      // the top) → leave alone.
      if (rect.top >= 80 && rect.top <= viewportH * 0.35) return;
      const desiredTop = viewportH * 0.2;
      window.scrollBy({ top: rect.top - desiredTop, behavior: 'smooth' });
    };
    document.addEventListener('focusin', handleFocus);
    return () => document.removeEventListener('focusin', handleFocus);
  }, []);

  // Focus Trip Start on initial mount for new TARs. Name of Traveler
  // is now hardcoded (read-only), so Trip Start is the first field the
  // user can actually interact with. MUI X v8's DatePicker uses the
  // accessible-field DOM structure (span sections, not a native input),
  // so we target the field container by name and let MUI delegate focus
  // to the first section (MM).
  useEffect(() => {
    if (id) return;
    const timer = setTimeout(() => {
      const el = document.querySelector('[name="eventStartDate"]');
      if (el && typeof el.focus === 'function') el.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, [id]);

  // Seed hardcoded PoP Start / PoP End for new TARs. Contract code is
  // hardcoded in the schema via `value: 'EUR001'` (text fields pick up
  // schema defaults through getInitialValue) but date fields don't, so
  // popStart / popEnd are seeded here. Existing TARs keep whatever was
  // persisted — don't overwrite history.
  useEffect(() => {
    if (id) return;
    setFormData((prev) => {
      const next = { ...prev };
      if (!prev.popStart) next.popStart = dayjs('2024-02-08');
      if (!prev.popEnd) next.popEnd = dayjs('2029-01-07');
      return next;
    });
  }, [id, setFormData]);

  // Auto-populate expense row quantities from the trip span:
  //   Lodging.numberOfDays = # of Nights
  //   Meals & Incidentals.numberOfDays = # of Days
  //   Rental Car.numberOfDays = # of Days
  // Manual edits stick via the "match-previous-auto" pattern: if the
  // row's current value still equals the last auto-written value (or is
  // empty), we overwrite; otherwise we leave the user's number alone.
  const lastAutoExpenseQtyRef = useRef({ nights: null, days: null });
  useEffect(() => {
    const expenses = formData?.expenses;
    if (!Array.isArray(expenses) || expenses.length === 0) return;
    const nights = formData?.numberOfNights || '';
    const days = formData?.numberOfTripDays || '';
    const last = lastAutoExpenseQtyRef.current;
    const targets = [
      { type: 'hotel', next: nights, lastAuto: last.nights },
      { type: 'mealsIncidentals', next: days, lastAuto: last.days },
      { type: 'car', next: days, lastAuto: last.days },
    ];
    let changed = false;
    const updated = expenses.map((row) => {
      const target = targets.find((t) => t.type === row.type);
      if (!target) return row;
      const current = row.numberOfDays || '';
      const userTouched = current !== '' && current !== target.lastAuto;
      if (userTouched) return row;
      if (current === target.next) return row;
      changed = true;
      return { ...row, numberOfDays: target.next };
    });
    if (changed) {
      lastAutoExpenseQtyRef.current = { nights, days };
      setFormData((prev) => ({ ...prev, expenses: updated }));
    }
  }, [
    formData?.numberOfNights,
    formData?.numberOfTripDays,
    formData?.expenses,
    setFormData,
  ]);

  // Auto-populate Meals & Incidentals total using the GSA per-diem
  // formula. The first and last day of a multi-day trip pay 75% of the
  // daily M&IE rate; full days in between pay 100%.
  //   1-day trip  → dailyMie * 0.75
  //   N-day trip  → (N - 2) * dailyMie + 2 * dailyMie * 0.75
  // The formula gives the TOTAL trip M&IE (Estimated Cost). The Rate /
  // Unit Cost column is then derived as total ÷ days — the blended
  // per-day rate — so the row reads as a normal Days × Rate = Total
  // line, while still respecting the 75% first/last-day reduction. The
  // row's days × rate auto-calc is skipped in ExpenseRows.updateExpense
  // for `mealsIncidentals` so neither field clobbers the other.
  // Manual edits stick via match-previous-auto.
  const lastAutoMieRef = useRef({ rate: null, total: null });
  useEffect(() => {
    const expenses = formData?.expenses;
    if (!Array.isArray(expenses) || expenses.length === 0) return;
    const idx = expenses.findIndex((e) => e.type === 'mealsIncidentals');
    if (idx === -1) return;
    const row = expenses[idx];
    const days = parseFloat(row.numberOfDays) || 0;
    const mie = parseFloat(formData?.dailyMieRate) || 0;
    let nextTotal = '';
    let nextRate = '';
    if (days > 0 && mie > 0) {
      const total = days === 1
        ? mie * 0.75
        : (days - 2) * mie + 2 * mie * 0.75;
      nextTotal = total.toFixed(2);
      nextRate = (total / days).toFixed(2);
    }
    const last = lastAutoMieRef.current;
    const currentTotal = row.totalEstimatedCost || '';
    const currentRate = row.itemUnitCost || '';
    const totalUserTouched = currentTotal !== '' && currentTotal !== last.total;
    const rateUserTouched = currentRate !== '' && currentRate !== last.rate;
    const newTotal = totalUserTouched ? currentTotal : nextTotal;
    const newRate = rateUserTouched ? currentRate : nextRate;
    if (newTotal === currentTotal && newRate === currentRate) return;
    const updated = [...expenses];
    updated[idx] = {
      ...row,
      totalEstimatedCost: newTotal,
      itemUnitCost: newRate,
    };
    lastAutoMieRef.current = {
      total: totalUserTouched ? last.total : nextTotal,
      rate: rateUserTouched ? last.rate : nextRate,
    };
    setFormData((prev) => ({ ...prev, expenses: updated }));
  }, [
    formData?.expenses,
    formData?.dailyMieRate,
    setFormData,
  ]);

  // Auto-populate Lodging rate/unit cost from formData.dailyLodgingRate.
  // Total is days × rate (no first/last-day adjustment, unlike M&IE).
  // Manual edits to the row's rate stick via match-previous-auto.
  const lastAutoLodgingRateRef = useRef(null);
  useEffect(() => {
    const expenses = formData?.expenses;
    if (!Array.isArray(expenses) || expenses.length === 0) return;
    const idx = expenses.findIndex((e) => e.type === 'hotel');
    if (idx === -1) return;
    const row = expenses[idx];
    const daysRaw = row.numberOfDays;
    const rateRaw = formData?.dailyLodgingRate;
    const days = parseFloat(daysRaw) || 0;
    const rate = parseFloat(rateRaw) || 0;
    const nextRate = rateRaw !== '' && rateRaw != null ? rate.toFixed(2) : '';
    const last = lastAutoLodgingRateRef.current;
    const currentRate = row.itemUnitCost || '';
    const rateUserTouched = currentRate !== '' && currentRate !== last;
    if (rateUserTouched) return;
    if (currentRate === nextRate) return;
    const daysFilled = daysRaw !== '' && daysRaw != null;
    const rateFilled = rateRaw !== '' && rateRaw != null;
    const nextTotal = (daysFilled || rateFilled) ? (days * rate).toFixed(2) : '';
    const updated = [...expenses];
    updated[idx] = {
      ...row,
      itemUnitCost: nextRate,
      totalEstimatedCost: nextTotal,
    };
    lastAutoLodgingRateRef.current = nextRate;
    setFormData((prev) => ({ ...prev, expenses: updated }));
  }, [
    formData?.expenses,
    formData?.dailyLodgingRate,
    setFormData,
  ]);

  // Default Due Date to 7 days before Trip Start (eventStartDate). The
  // ref tracks the last auto-computed value so manual user edits stick:
  // if formData.dueDate matches the previous auto value (or is empty),
  // we recompute when Trip Start changes. If the user typed something
  // else, we leave it alone.
  const lastAutoDueDateRef = useRef(null);
  useEffect(() => {
    if (!formData?.eventStartDate) return;
    const tripStartObj = dayjs(formData.eventStartDate);
    if (!tripStartObj.isValid()) return;
    const nextAutoDue = tripStartObj.subtract(7, 'day');
    setFormData((prev) => {
      const lastAuto = lastAutoDueDateRef.current;
      const userTouched =
        !!prev.dueDate && (!lastAuto || !dayjs(prev.dueDate).isSame(lastAuto, 'day'));
      if (userTouched) return prev;
      lastAutoDueDateRef.current = nextAutoDue;
      return { ...prev, dueDate: nextAutoDue };
    });
  }, [formData?.eventStartDate, setFormData]);

  const parsedRaw = useMemo(() => {
    const raw = tarResp?.rawFormData || tarResp?.RawFormData;
    return raw ? parseRawFormData(raw) : null;
  }, [tarResp]);

  // Set default POV mileage rate for new forms
  useEffect(() => {
    if (mileageRateInitialized || id) return;
    const expenses = formData?.expenses;
    if (!Array.isArray(expenses) || expenses.length === 0) return;
    const povIdx = expenses.findIndex(e => e.type === 'povMileage');
    if (povIdx === -1) return;
    if (expenses[povIdx].itemUnitCost) {
      setMileageRateInitialized(true);
      return;
    }
    const updated = [...expenses];
    updated[povIdx] = { ...updated[povIdx], itemUnitCost: '0.725' };
    setFormData(prev => ({ ...prev, expenses: updated }));
    setMileageRateInitialized(true);
  }, [id, mileageRateInitialized, formData?.expenses, setFormData]);

  // Compute subtotal and totalNTETripCost from expenses
  useEffect(() => {
    const expenses = formData?.expenses || [];
    const lineTotals = expenses.map(r => toNumber(r.totalEstimatedCost));
    const subtotal = lineTotals.reduce((a, b) => a + b, 0);

    setFormData(prev => {
      const next = { ...prev };
      const nextSubtotal = subtotal ? String(subtotal.toFixed(2)) : '';
      if (next.subtotal !== nextSubtotal) next.subtotal = nextSubtotal;
      if (next.totalNTETripCost !== nextSubtotal) next.totalNTETripCost = nextSubtotal;
      return next;
    });
  }, [formData?.expenses, setFormData]);

  // Load existing TAR data for editing
  useEffect(() => {
    if (!id) return;
    // The five auto-sync guards above (trip end, expense quantities,
    // M&IE, lodging rate, due date) compare against refs that reset to
    // null on mount, while a saved TAR hydrates pre-populated. Without
    // seeding, every loaded value reads as a manual override and
    // auto-sync never engages for the whole editing session (ASD-55).
    // Seed each ref with the value its formula WOULD produce from the
    // hydrated driver fields: loaded values that match their formula
    // resume syncing, while values that differ (a manual override from
    // a previous session) still read as user-touched and are preserved.
    const seedAutoSyncRefs = (data) => {
      const start = data.eventStartDate ? dayjs(data.eventStartDate) : null;
      const end = data.eventEndDate ? dayjs(data.eventEndDate) : null;
      const validStart = start && start.isValid() ? start : null;
      const validEnd = end && end.isValid() ? end : null;
      lastAutoTripEndRef.current = validStart ? validStart.add(7, 'day') : null;
      lastAutoDueDateRef.current = validStart
        ? validStart.subtract(7, 'day')
        : null;
      const nights = validStart && validEnd ? validEnd.diff(validStart, 'day') : 0;
      lastAutoExpenseQtyRef.current = {
        nights: nights > 0 ? String(nights) : null,
        days: nights > 0 ? String(nights + 1) : null,
      };
      const lodgingRaw = data.dailyLodgingRate;
      lastAutoLodgingRateRef.current =
        lodgingRaw !== '' && lodgingRaw != null
          ? (parseFloat(lodgingRaw) || 0).toFixed(2)
          : null;
      // Mirror the M&IE effect's GSA proration exactly, preferring the
      // persisted row's day count (it may itself be a kept override)
      // and falling back to the trip-span day count.
      const mieRow = Array.isArray(data.expenses)
        ? data.expenses.find((e) => e.type === 'mealsIncidentals')
        : null;
      const mieDays =
        parseFloat(mieRow?.numberOfDays) || (nights > 0 ? nights + 1 : 0);
      const mieRate = parseFloat(data.dailyMieRate) || 0;
      if (mieDays > 0 && mieRate > 0) {
        const total = mieDays === 1
          ? mieRate * 0.75
          : (mieDays - 2) * mieRate + 2 * mieRate * 0.75;
        lastAutoMieRef.current = {
          total: total.toFixed(2),
          rate: (total / mieDays).toFixed(2),
        };
      } else {
        lastAutoMieRef.current = { rate: null, total: null };
      }
    };
    if (!parsedRaw && tarResp) {
      const normalized = { ...tarResp };
      // In clone mode, remove old form version and identifiers so the latest version
      // from hiddenApi is preserved and a fresh new record is created on save
      if (isCloneMode) {
        delete normalized.formId;
        delete normalized.formVersionNumber;
        delete normalized.rawFormData;
        delete normalized.RawFormData;
        delete normalized.tarId;
        delete normalized.tarNumber;
        delete normalized.tarDate;
        delete normalized.dateCreated;
        delete normalized.lastModified;
        delete normalized.currentVersion;
      }
      // Convert standard date fields
      if (normalized.tarDate) normalized.tarDate = dayjs(normalized.tarDate);
      if (normalized.travelDate) normalized.travelDate = dayjs(normalized.travelDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);

      // Convert IMCS date fields
      ['returnDate', 'popStart', 'popEnd', 'eventStartDate', 'eventEndDate'].forEach(f => {
        if (normalized[f]) normalized[f] = dayjs(normalized[f]);
      });

      // Map estimates back to flat expenses array
      if (Array.isArray(normalized.estimates) && normalized.estimates.length > 0) {
        const lineItems = normalized.estimates[0]?.lineItems || [];
        // Expense types will be initialized by ExpenseRows from schema;
        // lineItems are mapped by description to populate values
        const expenseMap = {};
        lineItems.forEach(item => {
          const desc = (item.description || '').toUpperCase();
          expenseMap[desc] = item;
        });
        // Store for post-initialization mapping
        normalized._estimateLineItems = expenseMap;
      }

      normalized.workflowState = isCloneMode ? '' : (tarResp.workflowState ?? '');
      seedAutoSyncRefs(normalized);
      setFormData(prev => ({ ...prev, ...normalized }));
      return;
    }
    if (parsedRaw) {
      const normalized = { ...parsedRaw };
      if (isCloneMode) {
        delete normalized.formId;
        delete normalized.formVersionNumber;
        delete normalized.tarId;
        delete normalized.tarNumber;
        delete normalized.tarDate;
        delete normalized.dateCreated;
        delete normalized.lastModified;
        delete normalized.currentVersion;
      }
      if (normalized.tarDate) normalized.tarDate = dayjs(normalized.tarDate);
      if (normalized.travelDate) normalized.travelDate = dayjs(normalized.travelDate);
      if (normalized.dueDate) normalized.dueDate = dayjs(normalized.dueDate);
      ['returnDate', 'popStart', 'popEnd', 'eventStartDate', 'eventEndDate'].forEach(f => {
        if (normalized[f]) normalized[f] = dayjs(normalized[f]);
      });
      normalized.workflowState = isCloneMode ? '' : (tarResp?.workflowState ?? '');
      seedAutoSyncRefs(normalized);
      setFormData(prev => ({ ...prev, ...normalized }));
    }
  }, [id, isCloneMode, parsedRaw, tarResp, setFormData]);

  // ============================================================
  // Audit log (mirrors the expanded approvals ViewPanel)
  // ============================================================
  // `auditLog` lives on the approval-queue row, not on the TAR record
  // itself. Query the queue by workflowId and pluck the matching row's
  // auditLog. Skipped for brand-new drafts (no workflowId yet) so we
  // don't fire a wasteful 500-row fetch on empty forms. Hook is placed
  // ABOVE the loading-spinner early-return so hook order stays stable.
  const workflowId = tarResp?.workflowId;
  const { data: queueForAuditLog } = useGetApiApprovalQueueQuery(
    { approvalState: 'all', limit: 500, page: 1 },
    { skip: !workflowId },
  );
  const auditLog = useMemo(() => {
    if (!queueForAuditLog?.data || !workflowId) return [];
    const match = queueForAuditLog.data.find(
      (r) => r.workflowId === workflowId,
    );
    return match?.auditLog ?? [];
  }, [queueForAuditLog, workflowId]);

  if (isLoading || (id && isFetchingTAR && !parsedRaw)) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const handleFormDataChange = createFormDataChangeHandler(setFormData);

  const editableWorkflow = !formData?.workflowState || formData.workflowState === 'Pending';
  const isViewMode = isCloneMode ? false : (id ? searchParams.get('mode') !== 'edit' || !editableWorkflow : false);

  const handleCancel = () => {
    // Honor an explicit returnTo from the navigating link (e.g. View Form
    // from the library ViewPanel) first. Otherwise act like the browser
    // back button so callers from the approval queue, home widget, or any
    // future entry point land back on the page they came from instead of
    // being forced to the TAR list.
    const returnTo = location.state?.returnTo;
    if (returnTo) {
      navigate(returnTo);
    } else {
      navigate(-1);
    }
  };

  const handleSubmitSuccess = (result) => {
    if (result.ok && result.payload) {
      if (result.payload.tarId) {
        setSavedTarId(result.payload.tarId);
      }
    }
    if (result.ok && !result.skipNavigation) {
      setTimeout(() => navigate('/tar-imcs-list'), 2500);
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteTar({ id: id }).unwrap();
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'TAR deleted successfully',
        severity: 'success',
      });
      setTimeout(() => navigate('/tar-imcs-list'), 2500);
    } catch (error) {
      console.error('Failed to delete TAR', error);
      setDeleteDialogOpen(false);
      setSnackbar({
        open: true,
        message: 'Failed to delete TAR. Please try again.',
        severity: 'error',
      });
    }
  };

  const canDelete = id && editableWorkflow && formData?.workflowState !== undefined;

  // Status chip is shown only when working with a saved TAR (id present).
  // Brand-new drafts don't have a workflowState yet, so no chip.
  const showStatusChip = Boolean(id) && Boolean(formData?.workflowState);

  // Live trip summary — auto-fills as the user populates dates / location /
  // total. Plain inline derivation (no useMemo) because this component has
  // an early return above and a hook added here would change hook order
  // between loading and loaded renders. The work is cheap (3 reads + a
  // join) so memoization isn't needed.
  const tripSummarySegments = [];
  if (formData?.travelDate || formData?.returnDate) {
    const fmt = (d) => (d ? dayjs(d).format('MMM D') : '…');
    tripSummarySegments.push(
      `${fmt(formData?.travelDate)} – ${fmt(formData?.returnDate)}`,
    );
  }
  if (formData?.totalNTETripCost && Number(formData.totalNTETripCost) > 0) {
    const n = Number(formData.totalNTETripCost);
    tripSummarySegments.push(
      `Est. $${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
    );
  }
  const showTripSummary = tripSummarySegments.length > 0;

  return (
    <Box sx={{ width: '100%', maxWidth: { sm: '100%', md: '100%' }, p: 2 }}>
      <PageTitle sx={{ mb: 1 }}>Travel Authorization Request (TAR)</PageTitle>
      {/* Sticky top strip — anchors the Back button to the top edge so
          it's always reachable while scrolling long TARs, including
          brand-new drafts where the strip sits above the Instructions
          accordion. Status chip and trip summary ride along on the
          left when present. Approvals were removed from this form (all
          approve / reject actions live on the approval queue); this
          strip is now purely for navigation + at-a-glance status. */}
      <Box
        sx={(theme) => ({
          position: 'sticky',
          top: 0,
          zIndex: theme.zIndex.appBar,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? theme.palette.background.default
              : '#FFFFFF',
          borderBottom: '1px solid',
          borderColor: 'divider',
          mb: 1.5,
          mx: -2,
          px: 2,
          py: 1,
        })}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ flexWrap: 'wrap', rowGap: 0.75 }}
        >
          {showStatusChip && (
            <>
              <Typography
                variant="caption"
                sx={{
                  fontWeight: 700,
                  letterSpacing: 0.5,
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                }}
              >
                Status
              </Typography>
              <Chip
                size="small"
                label={formatWorkflowStatus(formData?.workflowState)}
                color={getStatusChipColor(formData?.workflowState)}
                sx={{ fontWeight: 600 }}
              />
              {formData?.tarNumber && (
                <Typography variant="caption" color="text.secondary">
                  · {formData.tarNumber}
                </Typography>
              )}
            </>
          )}
          {showTripSummary && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ ml: showStatusChip ? 2 : 0 }}
            >
              {tripSummarySegments.join(' · ')}
            </Typography>
          )}
          {/* Filler pushes the Back button to the right edge. Stack's
              `spacing` prop applies margin-left to non-first children,
              so `ml: 'auto'` alone wouldn't clear the added spacing. */}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleCancel}
            sx={{ boxShadow: 0 }}
          >
            Back
          </Button>
        </Stack>
      </Box>
      <Accordion
        disableGutters
        elevation={0}
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
          mb: 1.5,
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{ px: 2, '& .MuiAccordionSummary-content': { alignItems: 'center', gap: 1 } }}
        >
          <HelpOutlineIcon fontSize="small" color="primary" />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Instructions
          </Typography>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            This form authorizes official travel for IMCS contract work. Complete the required (*) fields and submit to enter the approval chain. You can save as a draft at any time. Fields shown in <strong>bold</strong> are autopopulated or system-generated and skipped on Tab.
          </Typography>
          <Divider sx={{ mb: 1.5 }} />
          <Stack spacing={1} sx={{ '& strong': { color: 'text.primary' } }}>
            <Typography variant="body2" color="text.secondary">
              <strong>Traveler Details</strong> — Name of Traveler is auto-filled from your login. Set Trip Start / Trip End and enter Duty Location. The cursor lands on Trip Start when you open a new TAR; # of Nights and # of Days calculate automatically from the trip dates.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Contract Details</strong> — COR, Contract, Task Order #, and Period of Performance are autopopulated for the IMCS contract (section shown muted). Select the <strong>Project</strong> you&apos;re travelling for from the dropdown — this is the only field in the section that requires input.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Contact Information</strong> — Enter Name, Phone Number, and Email Address for both the TDY Location POC and the Emergency Contact.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Location Details</strong> — Travel Destination, Postal Code, Daily Lodging Rate, Daily M&amp;IE Rate, and Trip Remarks. Look up current per-diem values via the linked DoD Per-Diem Rate Lookup.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>Estimated Expenses</strong> — Itemize all expected travel costs (airfare, lodging, meals, rental car, fuel, parking, mileage, etc.). Quantities for Lodging, M&amp;IE, and Rental Car auto-fill from the trip dates; the Lodging rate mirrors Daily Lodging Rate, and the M&amp;IE total uses the GSA 75%-first/last-day per-diem formula. Use <strong>+ Add Other Expense</strong> below the table to log any miscellaneous costs. The Total Estimated Trip Cost updates as you enter data.
            </Typography>
            <Typography variant="body2" color="text.secondary" component="div">
              <strong>Signatures &amp; Approval Chain</strong> — Order:{' '}
              <img
                src={OwlIcon}
                alt=""
                aria-hidden="true"
                style={{
                  height: '1.15em',
                  width: 'auto',
                  verticalAlign: '-0.25em',
                  marginRight: 4,
                }}
              />
              Submitter →{' '}
              <img
                src={OwlIcon}
                alt=""
                aria-hidden="true"
                style={{
                  height: '1.15em',
                  width: 'auto',
                  verticalAlign: '-0.25em',
                  marginRight: 4,
                }}
              />
              Program Administrator → 🇺🇸 APM → 🇺🇸 COR. Owl icons mark
              Tyto-side approvers; US-flag icons mark government
              approvers. Approval Due Date defaults to 7 days before
              Trip Start.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              <strong>After Submission</strong> — The TAR appears in the Approval Queue. You&apos;ll be notified as each approver acts on it.
            </Typography>
          </Stack>
        </AccordionDetails>
      </Accordion>
      {/* Outer form chrome: an outlined card with a soft grey wash so the
          whole TAR clearly reads as a single form on the page. Mode-aware
          so dark mode gets a subtle lighter tint instead of a glaring wash. */}
      <Paper
        variant="outlined"
        sx={(theme) => ({
          p: { xs: 2, md: 3 },
          borderRadius: 1.5,
          backgroundColor:
            theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.04)'
              : '#F4F5F7',
        })}
      >
        <DynamicForm
          schema={imcsTarFormSchema}
          formData={formData}
          setFormData={handleFormDataChange}
          onSubmit={handleSubmitSuccess}
          showSubmit={isCloneMode || !savedTarId}
          isEditMode={!isCloneMode && Boolean(savedTarId)}
          recordId={isCloneMode ? null : savedTarId}
          readOnly={isViewMode}
          // Back button lives in the sticky top strip above — don't
          // render the DynamicForm sticky-footer Cancel/Back so we
          // don't ship two Back affordances on the page.
          onCancel={undefined}
          onDelete={canDelete ? handleDeleteClick : undefined}
          submitMutation={submitTar}
          authorId={meData?.contact?.contactId}
        />
      </Paper>

      {/* Audit log — same treatment as the expanded approvals ViewPanel.
          Only renders once the TAR has entered a workflow (has a
          workflowId); brand-new drafts skip the fetch entirely. */}
      {workflowId && (
        <Box sx={{ mt: 3 }}>
          <AuditLog auditLog={auditLog} />
        </Box>
      )}

      <DeleteConfirmation
        open={deleteDialogOpen}
        title="Delete TAR"
        onClose={() => setDeleteDialogOpen(false)}
        content={{
          mainText: `
            Are you sure you want to delete TAR <strong>${formData?.tarNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
